import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Upload, CheckCircle, XCircle, AlertTriangle, Database, FileJson, Loader2, Wrench, Link2, Building2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminImportPage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Fix states
  const [fixingData, setFixingData] = useState(false);
  const [fixDataResult, setFixDataResult] = useState(null);
  const [fixingPublisher, setFixingPublisher] = useState(false);
  const [fixPublisherResult, setFixPublisherResult] = useState(null);
  const [movingToUwyg, setMovingToUwyg] = useState(false);
  const [moveToUwygResult, setMoveToUwygResult] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  
  // File states
  const [files, setFiles] = useState({
    manuscripts: null,
    publisher_titles: null,
    ephemeral_intelligence: null,
    publisher_authors: null,
  });

  // Check admin access on mount
  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      // Check migration status (this verifies admin access)
      // apiFetch returns { ok, status, json, ... } not a Response
      const result = await apiFetch(`${API}/api/admin/migration/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (result.ok && result.json) {
        setStatus(result.json);
        setIsAdmin(true);
      } else if (result.status === 403) {
        setError('Admin access required. You do not have permission to access this page.');
        setIsAdmin(false);
      } else {
        throw new Error(result.error?.message || 'Failed to verify admin access');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify admin access');
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (fieldName) => (e) => {
    const file = e.target.files?.[0] || null;
    setFiles(prev => ({ ...prev, [fieldName]: file }));
    setResult(null);
    setError(null);
  };

  const handleImport = async () => {
    // Check at least one file selected
    const selectedFiles = Object.entries(files).filter(([_, file]) => file !== null);
    if (selectedFiles.length === 0) {
      setError('Please select at least one JSON file to import.');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      
      // Add each selected file
      for (const [fieldName, file] of selectedFiles) {
        formData.append(fieldName, file);
      }

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/api/admin/migration/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Import failed');
      }

      setResult(data);
      
      // Refresh status after import
      await checkAdminAccess();
      
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Handle Fix Data Relationships
  const handleFixDataRelationships = async () => {
    setFixingData(true);
    setFixDataResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/api/admin/migration/fix-production-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fix failed');
      }

      setFixDataResult(data);
      
      // Refresh status
      await checkAdminAccess();
      
    } catch (err) {
      setError(err.message || 'Fix data relationships failed');
    } finally {
      setFixingData(false);
    }
  };

  // Handle Fix Publisher Access
  const handleFixPublisherAccess = async () => {
    setFixingPublisher(true);
    setFixPublisherResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/api/admin/migration/fix-publisher-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fix failed');
      }

      setFixPublisherResult(data);
      
      // Refresh status
      await checkAdminAccess();
      
    } catch (err) {
      setError(err.message || 'Fix publisher access failed');
    } finally {
      setFixingPublisher(false);
    }
  };

  // Handle Move Data to UWYG
  const handleMoveToUwyg = async () => {
    setMovingToUwyg(true);
    setMoveToUwygResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/api/admin/migration/move-data-to-uwyg`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Move failed');
      }

      setMoveToUwygResult(data);
      
      // Refresh status
      await checkAdminAccess();
      
    } catch (err) {
      setError(err.message || 'Move data to UWYG failed');
    } finally {
      setMovingToUwyg(false);
    }
  };

  // Handle Cleanup Test Data
  const handleCleanupTestData = async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/api/admin/migration/cleanup-test-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Cleanup failed');
      }

      setCleanupResult(data);
      
      // Refresh status
      await checkAdminAccess();
      
    } catch (err) {
      setError(err.message || 'Cleanup test data failed');
    } finally {
      setCleaningUp(false);
    }
  };

  const fileFields = [
    { key: 'manuscripts', label: 'Manuscripts', description: 'manuscripts_PROD.json' },
    { key: 'publisher_titles', label: 'Publisher Titles', description: 'publisher_titles_PROD.json' },
    { key: 'ephemeral_intelligence', label: 'Ephemeral Intelligence', description: 'ephemeral_intelligence_PROD.json' },
    { key: 'publisher_authors', label: 'Publisher Authors', description: 'publisher_authors_PROD.json' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-testid="admin-import-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 p-8" data-testid="admin-import-unauthorized">
        <div className="max-w-2xl mx-auto">
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              {error || 'You do not have permission to access this page.'}
            </AlertDescription>
          </Alert>
          <Button 
            className="mt-4" 
            variant="outline" 
            onClick={() => navigate('/')}
            data-testid="go-home-btn"
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-8" data-testid="admin-import-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Data Migration Import</h1>
            <p className="text-gray-400 mt-1">Import migration JSON files to MongoDB</p>
          </div>
          <Badge variant="outline" className="text-green-400 border-green-400">
            Admin Access
          </Badge>
        </div>

        {/* Current Status */}
        {status && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Database className="w-5 h-5" />
                Current Collection Counts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(status.collection_counts || {}).map(([name, count]) => (
                  <div key={name} className="bg-gray-800 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{count}</div>
                    <div className="text-xs text-gray-400 truncate">{name}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* File Upload Section */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Migration Files
            </CardTitle>
            <CardDescription>
              Select the JSON files from your migration package. You can import all files at once or individually.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fileFields.map(({ key, label, description }) => (
              <div key={key} className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor={key} className="text-gray-300">{label}</Label>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id={key}
                    type="file"
                    accept=".json"
                    onChange={handleFileChange(key)}
                    className="w-64 bg-gray-800 border-gray-700 text-gray-300 file:bg-gray-700 file:text-gray-300 file:border-0"
                    data-testid={`file-input-${key}`}
                  />
                  {files[key] && (
                    <FileJson className="w-5 h-5 text-green-400" />
                  )}
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-gray-800">
              <Button
                onClick={handleImport}
                disabled={importing || Object.values(files).every(f => f === null)}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Data
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" data-testid="import-error">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</AlertDescription>
          </Alert>
        )}

        {/* Result Display */}
        {result && (
          <Card className={`border ${result.success ? 'bg-green-950 border-green-800' : 'bg-yellow-950 border-yellow-800'}`} data-testid="import-result">
            <CardHeader className="pb-3">
              <CardTitle className={`flex items-center gap-2 ${result.success ? 'text-green-400' : 'text-yellow-400'}`}>
                {result.success ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Import Successful
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5" />
                    Import Completed with Warnings
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(result.imported || {}).map(([name, count]) => (
                  <div key={name} className="bg-gray-900 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{count}</div>
                    <div className="text-xs text-gray-400 truncate">{name}</div>
                  </div>
                ))}
              </div>

              {/* Details */}
              {result.details && result.details.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-300">Details by Collection:</h4>
                  {result.details.map((detail, idx) => (
                    <div key={idx} className="bg-gray-900 rounded p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">{detail.collection}</span>
                        <span className="text-gray-400">
                          {detail.inserted} inserted, {detail.updated} updated
                        </span>
                      </div>
                      {detail.errors && detail.errors.length > 0 && (
                        <div className="mt-2 text-xs text-red-400">
                          {detail.errors.slice(0, 3).map((err, i) => (
                            <div key={i}>• {err}</div>
                          ))}
                          {detail.errors.length > 3 && (
                            <div>...and {detail.errors.length - 3} more errors</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Global Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="bg-red-900/30 rounded p-3">
                  <h4 className="text-sm font-medium text-red-400 mb-2">Errors:</h4>
                  <div className="text-xs text-red-300 space-y-1">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <div key={i}>• {err}</div>
                    ))}
                    {result.errors.length > 5 && (
                      <div>...and {result.errors.length - 5} more errors</div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Instructions</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-400 space-y-2">
            <p>1. Download the migration package from <code className="text-blue-400">/newaudia_migration.zip</code></p>
            <p>2. Unzip the package to extract the JSON files</p>
            <p>3. Select the files you want to import (all 4 recommended)</p>
            <p>4. Click "Import Data" to start the import</p>
            <p>5. The import uses upsert - safe to run multiple times without duplicates</p>
          </CardContent>
        </Card>

        {/* Post-Import Fixes Section */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Post-Import Fixes
            </CardTitle>
            <CardDescription>
              After importing data, run these fixes to ensure everything is properly linked.
              Both operations are safe to run multiple times.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Fix 1: Data Relationships */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-blue-400" />
                    Step 1: Fix Data Relationships
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Links publisher records, syncs title names from manuscripts
                  </p>
                </div>
                <Button
                  onClick={handleFixDataRelationships}
                  disabled={fixingData}
                  variant={fixDataResult?.success ? "outline" : "default"}
                  className={fixDataResult?.success ? "border-green-600 text-green-400" : "bg-blue-600 hover:bg-blue-700"}
                  data-testid="fix-data-btn"
                >
                  {fixingData ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : fixDataResult?.success ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Done
                    </>
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Run Fix
                    </>
                  )}
                </Button>
              </div>
              
              {/* Fix Data Result */}
              {fixDataResult && (
                <div className={`rounded-lg p-3 text-sm ${fixDataResult.success ? 'bg-green-950/50 border border-green-800' : 'bg-red-950/50 border border-red-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {fixDataResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={fixDataResult.success ? 'text-green-400' : 'text-red-400'}>
                      {fixDataResult.success ? 'Fix Applied Successfully' : 'Fix Failed'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-300 space-y-1">
                    <div>Publisher owner fixed: {String(fixDataResult.fixes_applied?.publisher_owner_fixed)}</div>
                    <div>Titles synced: {fixDataResult.fixes_applied?.titles_synced || 0}</div>
                    <div>Titles already had names: {fixDataResult.fixes_applied?.titles_already_had_title || 0}</div>
                  </div>
                  {fixDataResult.fixes_applied?.details?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                        Show details ({fixDataResult.fixes_applied.details.length} actions)
                      </summary>
                      <div className="mt-1 text-xs text-gray-400 max-h-32 overflow-y-auto">
                        {fixDataResult.fixes_applied.details.map((d, i) => (
                          <div key={i}>• {d}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-800" />

            {/* Fix 2: Publisher Access */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-400" />
                    Step 2: Fix Publisher Access
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Consolidates publishers, removes duplicates, links to your account
                  </p>
                </div>
                <Button
                  onClick={handleFixPublisherAccess}
                  disabled={fixingPublisher}
                  variant={fixPublisherResult?.success ? "outline" : "default"}
                  className={fixPublisherResult?.success ? "border-green-600 text-green-400" : "bg-purple-600 hover:bg-purple-700"}
                  data-testid="fix-publisher-btn"
                >
                  {fixingPublisher ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : fixPublisherResult?.success ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Done
                    </>
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Run Fix
                    </>
                  )}
                </Button>
              </div>
              
              {/* Fix Publisher Result */}
              {fixPublisherResult && (
                <div className={`rounded-lg p-3 text-sm ${fixPublisherResult.success ? 'bg-green-950/50 border border-green-800' : 'bg-red-950/50 border border-red-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {fixPublisherResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={fixPublisherResult.success ? 'text-green-400' : 'text-red-400'}>
                      {fixPublisherResult.success ? 'Fix Applied Successfully' : 'Fix Failed'}
                    </span>
                  </div>
                  
                  {/* Final State */}
                  {fixPublisherResult.fixes_applied?.final_state && (
                    <div className="bg-gray-900 rounded p-2 mb-2">
                      <div className="text-xs text-gray-300">
                        <div className="font-medium text-white mb-1">Final Publisher State:</div>
                        <div>Name: {fixPublisherResult.fixes_applied.final_state.name}</div>
                        <div>Status: {fixPublisherResult.fixes_applied.final_state.status}</div>
                        <div className="text-gray-500 text-[10px] mt-1">
                          ID: {fixPublisherResult.fixes_applied.final_state.publisher_id}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-300 space-y-1">
                    <div>Empty publishers deleted: {fixPublisherResult.fixes_applied?.empty_publishers_deleted || 0}</div>
                    <div>Business details copied: {String(fixPublisherResult.fixes_applied?.business_details_copied)}</div>
                  </div>
                  
                  {fixPublisherResult.fixes_applied?.details?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                        Show details ({fixPublisherResult.fixes_applied.details.length} actions)
                      </summary>
                      <div className="mt-1 text-xs text-gray-400 max-h-32 overflow-y-auto">
                        {fixPublisherResult.fixes_applied.details.map((d, i) => (
                          <div key={i}>• {d}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>

            {/* Run Again Button */}
            {(fixDataResult?.success || fixPublisherResult?.success || moveToUwygResult?.success || cleanupResult?.success) && (
              <div className="pt-2 border-t border-gray-800">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFixDataResult(null);
                    setFixPublisherResult(null);
                    setMoveToUwygResult(null);
                    setCleanupResult(null);
                  }}
                  className="text-gray-400 hover:text-white"
                  data-testid="reset-fixes-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  Reset to Run Again
                </Button>
              </div>
            )}

            <div className="border-t border-gray-800 pt-4 mt-4" />

            {/* Step 3: Move Data to UWYG */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-orange-400" />
                    Step 3: Move Data to UWYG Publishing
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Moves all manuscripts, titles, authors to UWYG Publishing account
                  </p>
                </div>
                <Button
                  onClick={handleMoveToUwyg}
                  disabled={movingToUwyg}
                  variant={moveToUwygResult?.success ? "outline" : "default"}
                  className={moveToUwygResult?.success ? "border-green-600 text-green-400" : "bg-orange-600 hover:bg-orange-700"}
                  data-testid="move-uwyg-btn"
                >
                  {movingToUwyg ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Moving...
                    </>
                  ) : moveToUwygResult?.success ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Done
                    </>
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Move to UWYG
                    </>
                  )}
                </Button>
              </div>
              
              {/* Move to UWYG Result */}
              {moveToUwygResult && (
                <div className={`rounded-lg p-3 text-sm ${moveToUwygResult.success ? 'bg-green-950/50 border border-green-800' : 'bg-red-950/50 border border-red-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {moveToUwygResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={moveToUwygResult.success ? 'text-green-400' : 'text-red-400'}>
                      {moveToUwygResult.success ? 'Data Moved Successfully' : 'Move Failed'}
                    </span>
                  </div>
                  
                  {/* Target Publisher Info */}
                  {moveToUwygResult.fixes_applied?.target_publisher && (
                    <div className="bg-gray-900 rounded p-2 mb-2">
                      <div className="text-xs text-gray-300">
                        <div className="font-medium text-white mb-1">Target Publisher:</div>
                        <div>Name: {moveToUwygResult.fixes_applied.target_publisher.name}</div>
                        <div>Action: {moveToUwygResult.fixes_applied.target_publisher.action}</div>
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-300 space-y-1">
                    <div>Manuscripts moved: {moveToUwygResult.fixes_applied?.manuscripts_moved || 0}</div>
                    <div>Titles moved: {moveToUwygResult.fixes_applied?.titles_moved || 0}</div>
                    <div>Authors moved: {moveToUwygResult.fixes_applied?.authors_moved || 0}</div>
                  </div>
                  
                  {moveToUwygResult.fixes_applied?.details?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                        Show details ({moveToUwygResult.fixes_applied.details.length} actions)
                      </summary>
                      <div className="mt-1 text-xs text-gray-400 max-h-32 overflow-y-auto">
                        {moveToUwygResult.fixes_applied.details.map((d, i) => (
                          <div key={i}>• {d}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-800 pt-4 mt-4" />

            {/* Step 4: Cleanup Test Data */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    Step 4: Delete Test Manuscripts
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Removes "Durable Smoke Test", "P7 Test Book", "P8 Full Test Book" etc.
                  </p>
                </div>
                <Button
                  onClick={handleCleanupTestData}
                  disabled={cleaningUp}
                  variant={cleanupResult?.success ? "outline" : "destructive"}
                  className={cleanupResult?.success ? "border-green-600 text-green-400" : ""}
                  data-testid="cleanup-test-btn"
                >
                  {cleaningUp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : cleanupResult?.success ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Done
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Delete Test Data
                    </>
                  )}
                </Button>
              </div>
              
              {/* Cleanup Result */}
              {cleanupResult && (
                <div className={`rounded-lg p-3 text-sm ${cleanupResult.success ? 'bg-green-950/50 border border-green-800' : 'bg-red-950/50 border border-red-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {cleanupResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={cleanupResult.success ? 'text-green-400' : 'text-red-400'}>
                      {cleanupResult.success ? 'Test Data Deleted' : 'Cleanup Failed'}
                    </span>
                  </div>
                  
                  <div className="text-xs text-gray-300 space-y-1">
                    <div>Manuscripts deleted: {cleanupResult.fixes_applied?.manuscripts_deleted || 0}</div>
                    <div>Titles deleted: {cleanupResult.fixes_applied?.titles_deleted || 0}</div>
                    <div>EI docs deleted: {cleanupResult.fixes_applied?.ei_deleted || 0}</div>
                    <div className="text-green-400 font-medium mt-2">
                      Real manuscripts remaining: {cleanupResult.fixes_applied?.remaining_real_manuscripts || 0}
                    </div>
                  </div>
                  
                  {cleanupResult.fixes_applied?.deleted_titles?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                        Show deleted titles ({cleanupResult.fixes_applied.deleted_titles.length})
                      </summary>
                      <div className="mt-1 text-xs text-gray-400 max-h-32 overflow-y-auto">
                        {cleanupResult.fixes_applied.deleted_titles.map((t, i) => (
                          <div key={i}>• {t}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
